import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography, Grid, Paper, useTheme, useMediaQuery, CircularProgress, LinearProgress } from '@mui/material';
import { People, Payments, Savings, CreditCard, AccountBalance, Apartment, Agriculture, EmojiEvents, LocalAtm, MonetizationOn } from '@mui/icons-material';
import { getStatistics } from '../models/Statistics';
import { collection, query, orderBy, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

// Fonction pour animer le comptage des chiffres
const CountUp = ({ end, duration = 2000, prefix = '', suffix = '' }) => {
  const [count, setCount] = useState(0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  useEffect(() => {
    let startTime;
    let animationFrame;
    
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      // Fonction d'easing pour une animation plus naturelle (easeOutQuad)
      const easeProgress = 1 - (1 - progress) * (1 - progress);
      
      setCount(Math.floor(easeProgress * end));
      
      if (progress < 1) {
        animationFrame = requestAnimationFrame(step);
      }
    };
    
    animationFrame = requestAnimationFrame(step);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration]);
  
  return (
    <Typography 
      variant="h3" 
      component="div" 
      sx={{ 
        fontWeight: 700,
        fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
        lineHeight: 1.2,
        wordBreak: 'break-word',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '0.2rem'
      }}
    >
      {prefix && <span>{prefix}&nbsp;</span>}
      <span>{count.toLocaleString()}</span>
      {suffix && <span>{suffix}</span>}
    </Typography>
  );
};

const StatsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  
  // État pour stocker les statistiques depuis Firestore
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Fonction pour obtenir l'icône correspondante
  const getIcon = (iconName) => {
    const icons = {
      'People': <People sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'Payments': <Payments sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'Savings': <Savings sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'CreditCard': <CreditCard sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'AccountBalance': <AccountBalance sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'Apartment': <Apartment sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'Agriculture': <Agriculture sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'EmojiEvents': <EmojiEvents sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'LocalAtm': <LocalAtm sx={{ fontSize: 50, color: theme.palette.primary.main }} />,
      'MonetizationOn': <MonetizationOn sx={{ fontSize: 50, color: theme.palette.primary.main }} />
    };
    return icons[iconName] || <People sx={{ fontSize: 50, color: theme.palette.primary.main }} />;
  };
  
  // État pour gérer les erreurs
  const [error, setError] = useState(null);

  // Charger les statistiques depuis Firestore
  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        setError(null); // Réinitialiser l'erreur à chaque tentative
        const statsQuery = query(collection(db, 'statistics'), orderBy('order', 'asc'));
        const statsSnapshot = await getDocs(statsQuery);
        
        if (statsSnapshot.empty) {
          // Si aucune statistique n'existe, utiliser des valeurs par défaut
          setStats([
            {
              id: 'default1',
              icon: 'People',
              value: 1000,
              prefix: '+ de',
              suffix: '',
              label: 'Membres dans notre communauté',
              description: 'Agriculteurs, experts et passionnés réunis'
            },
            {
              id: 'default2',
              icon: 'Payments',
              value: 450000,
              prefix: '',
              suffix: ' FCFA',
              label: 'Cotisations WhatsApp',
              description: 'Fonds collectés pour soutenir nos projets'
            },
            {
              id: 'default3',
              icon: 'Savings',
              value: 40,
              prefix: '+',
              suffix: ' €',
              label: 'Cagnotte Leetchi et AirtelMoney',
              description: 'Financement participatif en croissance'
            }
          ]);
        } else {
          // Sinon, utiliser les statistiques de Firestore
          const statsData = statsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setStats(statsData);
        }
      } catch (err) {
        console.error('Erreur lors du chargement des statistiques:', err);
        setError('Impossible de charger les statistiques. Veuillez rafraîchir la page ou réessayer plus tard.');
        // En cas d'erreur, utiliser des valeurs par défaut
        setStats([
          {
            id: 'default1',
            icon: 'People',
            value: 1000,
            prefix: '+ de',
            suffix: '',
            label: 'Membres dans notre communauté',
            description: 'Agriculteurs, experts et passionnés réunis'
          },
          {
            id: 'default2',
            icon: 'Payments',
            value: 450000,
            prefix: '',
            suffix: ' FCFA',
            label: 'Cotisations WhatsApp',
            description: 'Fonds collectés pour soutenir nos projets'
          },
          {
            id: 'default3',
            icon: 'Savings',
            value: 40,
            prefix: '+',
            suffix: ' €',
            label: 'Cagnotte Leetchi et AirtelMoney',
            description: 'Financement participatif en croissance'
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchStats();
  }, []);
  
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => {
      if (sectionRef.current) observer.unobserve(sectionRef.current);
    };
  }, []);
  
  return (
    <Box 
      ref={sectionRef}
      sx={{
        py: { xs: 6, md: 8 },
        backgroundColor: '#f8f9fa',
        borderTop: '1px solid #e0e0e0',
        borderBottom: '1px solid #e0e0e0'
      }}
    >
      <Container maxWidth="lg">
        <Typography 
          variant="h4" 
          component="h2" 
          align="center" 
          gutterBottom
          sx={{ 
            mb: 4, 
            fontWeight: 700,
            color: theme.palette.primary.main,
            textTransform: 'uppercase',
            letterSpacing: 1
          }}
        >
          Notre Impact en Chiffres
        </Typography>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Grid container spacing={4} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={4} key={stat.id || index}>
                <Paper 
                  elevation={3} 
                  sx={{
                    p: { xs: 2, sm: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    borderRadius: 2,
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 20px rgba(0, 0, 0, 0.1)'
                    },
                    minWidth: 0,
                    maxWidth: 350,
                    mx: 'auto',
                  }}
                >
                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    {getIcon(stat.icon)}
                  </Box>
                  <Box sx={{ mb: 1, width: '100%' }}>
                    {isVisible ? (
                      <CountUp end={stat.value} duration={2000} prefix={stat.prefix} suffix={stat.suffix} />
                    ) : (
                      <Typography 
                        variant="h3" 
                        component="div" 
                        sx={{ 
                          fontWeight: 700,
                          fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
                          lineHeight: 1.2,
                          wordBreak: 'break-word',
                          display: 'flex',
                          flexWrap: 'wrap',
                          justifyContent: 'center',
                          alignItems: 'center',
                          gap: '0.2rem'
                        }}
                      >
                        {stat.prefix && <span>{stat.prefix}&nbsp;</span>}
                        <span>0</span>
                        {stat.suffix && <span>{stat.suffix}</span>}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="h6"
                    component="h3"
                    sx={{
                      mb: 1,
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                      fontSize: { xs: '1.1rem', sm: '1.25rem' },
                      wordBreak: 'break-word',
                    }}
                  >
                    {stat.label}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 'auto', fontSize: { xs: '0.95rem', sm: '1rem' }, wordBreak: 'break-word', mb: stat.target ? 2 : 0 }}
                  >
                    {stat.description}
                  </Typography>
                  
                  {/* Barre de progression si un objectif est défini */}
                  {stat.target && (
                    <Box sx={{ width: '100%', mt: 1 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          Progression
                        </Typography>
                        <Typography variant="caption" fontWeight="bold" color="primary.main">
                          {Math.min(Math.round((stat.value / stat.target) * 100), 100)}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={Math.min((stat.value / stat.target) * 100, 100)} 
                        sx={{ 
                          height: 8, 
                          borderRadius: 4,
                          backgroundColor: 'rgba(1, 69, 39, 0.1)',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: '#014527',
                            borderRadius: 4
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                        Objectif: {stat.prefix || ''}{stat.target.toLocaleString()}{stat.suffix || ''}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default StatsSection;